import {
  faFlag,
  faHotel,
  faTrain,
  faPlane,
} from "@fortawesome/free-solid-svg-icons";
import { SearchResult } from "@search/api/types";
import clsx from "clsx";
import pluralize from "pluralize";

import { ListItem } from "@/components/atoms/ListItem";
import { List } from "@/components/compounds/List";
import LeShuttleLogo from "@/components/static/LeShuttleLogo";
import { ColorEnum } from "@/enums";
import { type CommonProps } from "@/types";
import { createId } from "@/utils/helpers";

import styles from "./PackageDetails.module.css";

const ref = "package-details";

interface PackageDetailsProps extends Partial<CommonProps>, SearchResult {}

function joiner(array: Array<string>, inclusive?: boolean) {
  const spans = array.map((element, index, { length }) => {
    const content = `${element}${index < length - 2 ? ", " : ""}`;

    return <span key={`${element}_${index}`}>{content}</span>;
  });
  const last = spans.pop() || null;

  return spans.length > 0 ? (
    <>
      {spans}
      {inclusive ? " and " : " or "}
      {last}
    </>
  ) : (
    last
  );
}

function renderAccommodation(accommodation: SearchResult["accommodation"]) {
  return accommodation.map((item, index, { length }) => {
    const multiple = length > 1;
    const boardBasis = `${multiple ? " " : ""}${item.boardBasis}`;
    const hotelName = multiple ? (
      <>
        {" at "}
        <span>{item.hotelName}</span>
      </>
    ) : (
      ""
    );
    const nights = multiple ? pluralize("Night", item.nights, true) : "";

    return (
      <ListItem key={`accommodation_${index}`} icon={faHotel}>
        <p>
          {nights}
          {boardBasis}
          {hotelName}
        </p>
      </ListItem>
    );
  });
}

function renderAllocated(
  allocatedRounds: SearchResult["allocatedRounds"] = [],
) {
  return allocatedRounds.length > 0
    ? allocatedRounds.map((allocatedRound, index) => (
        <ListItem key={`allocated_${index}`} icon={faFlag}>
          <p>
            {`${pluralize("Round", allocatedRound.rounds, true)} at `}
            <span>{allocatedRound.courseName}</span>
          </p>
        </ListItem>
      ))
    : null;
}

function renderUnallocated(
  unallocatedRounds: SearchResult["unallocatedRounds"] = {
    courses: [],
    rounds: 0,
  },
) {
  return unallocatedRounds?.courses.length > 0 ? (
    <ListItem icon={faFlag}>
      <p>
        {`${pluralize("Round", unallocatedRounds?.rounds, true)} at `}
        {joiner(unallocatedRounds?.courses)}
      </p>
    </ListItem>
  ) : null;
}

function renderUnlimited(
  unlimitedRounds: SearchResult["unlimitedRounds"] = [],
) {
  return unlimitedRounds.length > 0 ? (
    <ListItem icon={faFlag}>
      <p>Unlimited Rounds at {joiner(unlimitedRounds)}</p>
    </ListItem>
  ) : null;
}

function renderTransport(transportation: SearchResult["transportation"]) {
  if (transportation.flights) {
    return (
      <ListItem icon={faPlane}>
        <p>Flights</p>
      </ListItem>
    );
  } else if (transportation.eurotunnel) {
    return (
      <ListItem icon={faTrain}>
        <p>
          <span className="sr-only">Le Shuttle</span>
          <LeShuttleLogo />
        </p>
      </ListItem>
    );
  } else {
    return null;
  }
}

function usePackageDetails(props: PackageDetailsProps) {
  const className = clsx(styles[ref], props.className);
  const { color = ColorEnum.GREEN, id = createId(ref) } = props;

  return {
    className,
    color,
    id,
  };
}

function PackageDetails(props: PackageDetailsProps) {
  const {
    accommodation,
    allocatedRounds,
    unallocatedRounds,
    unlimitedRounds,
    transportation,
  } = props;
  const { className, color, id } = usePackageDetails(props);

  return (
    <List className={className} color={color} compact iconTop id={id}>
      {renderAccommodation(accommodation)}
      {renderAllocated(allocatedRounds)}
      {renderUnallocated(unallocatedRounds)}
      {renderUnlimited(unlimitedRounds)}
      {renderTransport(transportation)}
    </List>
  );
}

export default PackageDetails;
export { PackageDetails, type PackageDetailsProps };
